import { t } from 'i18next';
import { 
  ICourse, 
  ICourseData, 
  ICourseParams, 
  ICourseChapter, 
  ICourseChapterData, 
  ICourseChapterParams, 
  ITableRes, 
  ICourseChapterMaterial, 
  ICourseChapterMaterialParams, 
  ICourseChapterItem,
  ICourseComponentParams,
  ICourseComponent,
  ICourseComponentData,
  ICourseItem,
  ICourseSetupMainData,
  IDefaultCourseComponent
} from 'types'
import { baseApi } from './baseApi'
import { MAX_PER_PAGE } from 'utils/constants';

export const coursesAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    //Course
    GET_courses: builder.query<ITableRes<ICourseItem>, ICourseParams>({
      query: (params) => ({
        url: '/courses/',
        params,
      }),
      providesTags: ['courses'],
    }),
    GET_courseById: builder.query<ICourse, string>({
      query: (id) => {
        return {
          url: `/courses/${id}/`,
        }
      },
      providesTags: ['course'],
    }),
    POST_course: builder.mutation<ICourse, ICourseData>({
      query: (body) => ({
        url: `/courses/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['courses', 'course'],
    }),
    PUT_course: builder.mutation<ICourse, { id: string; body: ICourseData }>({
      query: ({ id, body }) => ({
        url: `/courses/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['courses', 'course'],
    }),
    DELETE_course: builder.mutation<ICourse, string>({
      query: (id) => ({
        url: `/courses/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['courses'],
    }),

    //CourseChapter
    GET_courseChapters: builder.query<ITableRes<ICourseChapterItem>, ICourseChapterParams>({
      query: (params) => ({
        url: '/course-chapters/',
        params,
      }),
      providesTags: ['course-chapters'],
    }),
    GET_courseChapterById: builder.query<ICourseChapter, string>({
      query: (id) => {
        return {
          url: `/course-chapters/${id}/`,
        }
      },
      providesTags: ['course-chapters'],
    }),
    POST_courseChapter: builder.mutation<ICourseChapter, ICourseChapterData>({
      query: (body) => ({
        url: `/course-chapters/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['course-chapters', 'course-chapter'],
    }),
    PUT_courseChapter: builder.mutation<ICourseChapter, { id: string; body: ICourseChapter }>({
      query: ({ id, body }) => ({
        url: `/course-chapters/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['course-chapters', 'course-chapter'],
    }),
    DELETE_courseChapter: builder.mutation<ICourseChapter, string>({
      query: (id) => ({
        url: `/course-chapters/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['course-chapters'],
    }),

    //CourseChapterMaterial
    GET_courseChapterMaterials: builder.query<ITableRes<ICourseChapterMaterial>, ICourseChapterMaterialParams>({
      query: (params) => ({
        url: '/course-chapter-materials/',
        params,
      }),
      providesTags: ['course-chapter-materials'],
    }),

    //Select
    GET_courseSelect: builder.query<any, any>({
      query: (params) => ({
        url: '/courses/select/',
        params: {...params, page_size: MAX_PER_PAGE},
      }),
      transformResponse: (response: any) => {
        const hasMore = Boolean(response?.next)
        const count = response?.count || 0
        return {
          list: response?.results || [],
          count,
          hasMore,
        }
      },
      providesTags: ['course-select'],
    }),
    GET_courseChapterSelect: builder.query<any, {group_course: number}>({
      query: (params) => ({
        url: '/course-chapters/select/',
        params: {...params, page_size: MAX_PER_PAGE},
      }),
      transformResponse: (response: any) => {
        const hasMore = Boolean(response?.next)
        const count = response?.count || 0
        return {
          list: response?.results || [],
          count,
          hasMore,
        }
      },
      providesTags: ['course-chapters-select'],
    }),

    //CourseComponents
    GET_courseComponents: builder.query<ITableRes<ICourseComponent>, ICourseComponentParams>({
      query: (params) => ({
        url: '/course-components/',
        params,
      }),
      providesTags: ['course-components'],
    }),
    GET_courseComponentById: builder.query<ICourseComponent, string>({
      query: (id) => {
        return {
          url: `/course-components/${id}/`,
        }
      },
      transformResponse: (response: any): any => {
        return {
         ...response,
         score_type:  response?.score_type ? {id: response?.score_type, title: t(`common.${response?.score_type}`)} : null,
        }
      },
      providesTags: ['course-components'],
    }),
    POST_courseComponent: builder.mutation<ICourseComponent, ICourseComponentData>({
      query: (body) => ({
        url: `/course-components/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['course-components', 'course-component'],
    }),
    POST_updateDefaultCourseComponents: builder.mutation<IDefaultCourseComponent[], any[]>({
      query: (body) => ({
        url: `/course-components/update-defaults/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['course-components'],
    }),
    PUT_courseComponent: builder.mutation<ICourseComponent, { id: string; body: ICourseComponent }>({
      query: ({ id, body }) => ({
        url: `/course-components/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['course-components', 'course-component'],
    }),
    DELETE_courseComponent: builder.mutation<ICourseComponent, string>({
      query: (id) => ({
        url: `/course-components/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['course-components'],
    }),

    //Select
    GET_courseComponentsSelect: builder.query<any, ICourseComponentParams>({
      query: (params) => ({
        url: '/course-components/select/',
        params: {...params, page_size: MAX_PER_PAGE},
      }),
      transformResponse: (response: any) => {
        const hasMore = Boolean(response?.next)
        const count = response?.count || 0
        return {
          list: response?.results || [],
          count,
          hasMore,
        }
      },
      providesTags: ['course-components-select'],
    }),

    //Course Setup Main
    GET_courseSetupMain: builder.query<ITableRes<ICourseComponent>, void>({
      query: () => ({
        url: '/course-setup-main/',
      }),
      providesTags: ['course-setup-main'],
    }),

    POST_courseSetupMain: builder.mutation<ICourseSetupMainData, ICourseSetupMainData>({
      query: (body) => ({
        url: `/course-setup-main/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['course-setup-main'],
    }),
  }),
})

export const { 
  useGET_coursesQuery, 
  useGET_courseByIdQuery, 
  usePOST_courseMutation, 
  usePUT_courseMutation, 
  useDELETE_courseMutation, 
  useGET_courseSelectQuery, 
  useGET_courseChaptersQuery, 
  useGET_courseChapterByIdQuery, 
  usePOST_courseChapterMutation, 
  usePUT_courseChapterMutation, 
  useDELETE_courseChapterMutation,
  useGET_courseChapterMaterialsQuery, 
  useGET_courseChapterSelectQuery, 
  useGET_courseComponentsQuery, 
  useGET_courseComponentByIdQuery, 
  usePOST_courseComponentMutation, 
  usePOST_updateDefaultCourseComponentsMutation, 
  usePUT_courseComponentMutation, 
  useDELETE_courseComponentMutation,
  useGET_courseComponentsSelectQuery,
  useGET_courseSetupMainQuery, 
  usePOST_courseSetupMainMutation, 
} = coursesAPI
